import React, { useEffect, useState } from 'react';
import { supabase } from '../../../supabaseClient';
import cfpDataRaw from '../../../data/cfp.json';

export default function CFPTab() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pageId, setPageId] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [showAdvancedJSON, setShowAdvancedJSON] = useState(false);
    const [jsonString, setJsonString] = useState('');
    
    const [formData, setFormData] = useState({
        hero: { tagline: '', titleLine1: '', titleLine2: '', subtitle: '', deadline: '', tracks: [] },
        tracks: [],
        timeline: [],
        cta: { title: '', desc: '', email: '', buttonText: '', buttonLink: '' }
    });

    useEffect(() => {
        fetchPageContent();
    }, []);

    const fetchPageContent = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('cfp_page_content')
            .select('*')
            .eq('page_name', 'cfp-page')
            .single();

        if (error && error.code !== 'PGRST116') { 
            if (error.code === '42P01') {
                setError("Table 'cfp_page_content' does not exist in Supabase. Please create it first using the provided SQL.");
            } else {
                setError(error.message);
            }
        } else if (data) {
            setPageId(data.id);
            setFormData(data.data);
            setError(null);
        }
        setLoading(false);
    };

    const handleImportJson = async () => {
        if (!window.confirm("This will import page content from cfp.json. Are you sure?")) return;
        setLoading(true);
        
        try {
            const { error } = await supabase.from('cfp_page_content').upsert([{
                page_name: 'cfp-page',
                data: cfpDataRaw
            }], { onConflict: 'page_name' });
            
            if (error) throw error;
            
            alert('Import successful!');
            fetchPageContent();
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        
        // Cleanup arrays if they were edited via textareas
        const payloadData = { ...formData };
        if (typeof payloadData.hero.tracks === 'string') {
            payloadData.hero.tracks = payloadData.hero.tracks.split('\n').filter(s => s.trim() !== '');
        }

        if (pageId) {
            const { error: updateError } = await supabase
                .from('cfp_page_content')
                .update({ data: payloadData })
                .eq('id', pageId);
            if (updateError) setError(updateError.message);
        } else {
            const { data, error: insertError } = await supabase
                .from('cfp_page_content')
                .insert([{ page_name: 'cfp-page', data: payloadData }])
                .select();
            if (insertError) {
                setError(insertError.message);
            } else if (data && data.length > 0) {
                setPageId(data[0].id);
            }
        }
        
        if (!error) {
            alert('Page content saved successfully!');
            setIsEditing(false);
            fetchPageContent();
        } else {
            setLoading(false);
        }
    };

    // Track helpers
    const addTrack = () => {
        setFormData({
            ...formData,
            tracks: [...formData.tracks, { icon: 'fa-crosshairs', title: 'New Track', desc: '' }]
        });
    };
    
    const updateTrack = (index, field, value) => {
        const newTracks = [...formData.tracks];
        newTracks[index][field] = value;
        setFormData({ ...formData, tracks: newTracks });
    };

    const removeTrack = (index) => {
        const newTracks = [...formData.tracks];
        newTracks.splice(index, 1);
        setFormData({ ...formData, tracks: newTracks });
    };

    // Timeline helpers
    const addTimelineEvent = () => {
        setFormData({
            ...formData,
            timeline: [...formData.timeline, { date: 'New Date', label: 'New Event' }]
        });
    };
    
    const updateTimelineEvent = (index, field, value) => {
        const newTimeline = [...formData.timeline];
        newTimeline[index][field] = value;
        setFormData({ ...formData, timeline: newTimeline });
    };

    const removeTimelineEvent = (index) => {
        const newTimeline = [...formData.timeline];
        newTimeline.splice(index, 1);
        setFormData({ ...formData, timeline: newTimeline });
    };

    if (!pageId && !loading && !error) {
        return (
            <div className="card shadow-lg border-0 rounded-4 text-center py-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <h4 className="text-white mb-3">No CFP Content Found</h4>
                <p style={{ color: 'rgba(255,255,255,0.5)' }}>Please import the initial content from JSON.</p>
                <div>
                    <button onClick={handleImportJson} className="btn btn-outline-warning">
                        <i className="fa fa-download me-2"></i>Import from JSON
                    </button>
                </div>
            </div>
        );
    }

    if (isEditing) {
        return (
            <div className="card shadow-lg border-0 rounded-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="card-header border-0 bg-transparent py-4 px-4">
                    <div className="d-flex justify-content-between align-items-center">
                        <h4 className="text-white m-0" style={{ fontFamily: 'var(--eventflow-font)', fontWeight: 700 }}>Edit CFP Content</h4>
                        <button 
                            type="button" 
                            className="btn btn-sm btn-outline-warning"
                            onClick={() => {
                                if (!showAdvancedJSON) {
                                    setJsonString(JSON.stringify(formData, null, 2));
                                }
                                setShowAdvancedJSON(!showAdvancedJSON);
                            }}
                        >
                            <i className="fas fa-code me-2"></i> {showAdvancedJSON ? "Hide Advanced JSON" : "Show Advanced JSON Editor"}
                        </button>
                    </div>
                </div>
                
                <div className="card-body px-4 pb-4 text-white">
                    {error && <div className="alert alert-danger">{error}</div>}
                    
                    <form onSubmit={handleSubmit}>
                        {showAdvancedJSON ? (
                            <div className="col-12 mb-4">
                                <div className="alert alert-warning mb-3">
                                    <strong>Warning:</strong> Be very careful when editing raw JSON. A syntax error will break the layout.
                                </div>
                                <label className="form-label">Raw Data (JSON)</label>
                                <textarea 
                                    className="form-control bg-dark text-warning" 
                                    style={{ border: '1px solid rgba(255,255,255,0.2)', fontFamily: 'monospace' }} 
                                    rows="20"
                                    value={jsonString}
                                    onChange={e => setJsonString(e.target.value)}
                                    onBlur={e => {
                                        try {
                                            const parsed = JSON.parse(e.target.value);
                                            setFormData(parsed);
                                        } catch(err) {
                                            alert("Invalid JSON format! Please fix any missing quotes or commas before saving.");
                                        }
                                    }}
                                ></textarea>
                            </div>
                        ) : (
                            <>
                                <h5 className="mb-3 mt-2" style={{ color: 'var(--eventflow-primary)' }}>Hero Section</h5>
                                <div className="row g-3 mb-4">
                                    <div className="col-md-4">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Tagline / Eyebrow</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.hero?.tagline || ''} 
                                            onChange={e => setFormData({...formData, hero: {...formData.hero, tagline: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Title Line 1</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.hero?.titleLine1 || ''} 
                                            onChange={e => setFormData({...formData, hero: {...formData.hero, titleLine1: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Title Line 2 (Colored)</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.hero?.titleLine2 || ''} 
                                            onChange={e => setFormData({...formData, hero: {...formData.hero, titleLine2: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Subtitle / Description</label>
                                        <textarea className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} rows="2" 
                                            value={formData.hero?.subtitle || ''} 
                                            onChange={e => setFormData({...formData, hero: {...formData.hero, subtitle: e.target.value}})} 
                                        ></textarea>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Deadline text</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.hero?.deadline || ''} 
                                            onChange={e => setFormData({...formData, hero: {...formData.hero, deadline: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Tracks (Pills) - One per line</label>
                                        <textarea className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} rows="3" 
                                            value={Array.isArray(formData.hero?.tracks) ? formData.hero.tracks.join('\n') : formData.hero?.tracks} 
                                            onChange={e => setFormData({...formData, hero: {...formData.hero, tracks: e.target.value}})} 
                                        ></textarea>
                                    </div>
                                </div>
                                
                                <hr style={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                                
                                <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
                                    <h5 className="m-0" style={{ color: 'var(--eventflow-primary)' }}>Tracks Details</h5>
                                    <button type="button" className="btn btn-sm btn-outline-info" onClick={addTrack}>+ Add Track</button>
                                </div>
                                
                                {formData.tracks.map((track, idx) => (
                                    <div className="card bg-transparent mb-3" style={{ border: '1px dashed rgba(255,255,255,0.2)' }} key={idx}>
                                        <div className="card-body p-3">
                                            <div className="d-flex justify-content-between mb-2">
                                                <h6 className="m-0 text-white-50">Track {idx + 1}</h6>
                                                <button type="button" className="btn btn-sm btn-danger py-0 px-2" onClick={() => removeTrack(idx)}><i className="fas fa-trash"></i></button>
                                            </div>
                                            <div className="row g-2">
                                                <div className="col-md-4">
                                                    <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Icon (e.g. fa-cloud)</label>
                                                    <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                        value={track.icon} onChange={e => updateTrack(idx, 'icon', e.target.value)} />
                                                </div>
                                                <div className="col-md-8">
                                                    <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Title</label>
                                                    <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                        value={track.title} onChange={e => updateTrack(idx, 'title', e.target.value)} />
                                                </div>
                                                <div className="col-12">
                                                    <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Description</label>
                                                    <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                        value={track.desc} onChange={e => updateTrack(idx, 'desc', e.target.value)} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <hr style={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                                
                                <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
                                    <h5 className="m-0" style={{ color: 'var(--eventflow-primary)' }}>Timeline Events</h5>
                                    <button type="button" className="btn btn-sm btn-outline-info" onClick={addTimelineEvent}>+ Add Event</button>
                                </div>
                                
                                {formData.timeline.map((item, idx) => (
                                    <div className="row g-2 mb-2 align-items-end" key={idx}>
                                        <div className="col-md-4">
                                            <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Date</label>
                                            <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                value={item.date} onChange={e => updateTimelineEvent(idx, 'date', e.target.value)} />
                                        </div>
                                        <div className="col-md-7">
                                            <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Label</label>
                                            <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                value={item.label} onChange={e => updateTimelineEvent(idx, 'label', e.target.value)} />
                                        </div>
                                        <div className="col-md-1 text-end">
                                            <button type="button" className="btn btn-sm btn-danger mb-1" onClick={() => removeTimelineEvent(idx)}><i className="fas fa-trash"></i></button>
                                        </div>
                                    </div>
                                ))}

                                <hr style={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                                
                                <h5 className="mb-3 mt-4" style={{ color: 'var(--eventflow-primary)' }}>Call To Action Box</h5>
                                <div className="row g-3">
                                    <div className="col-md-12">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Title</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.cta?.title || ''} 
                                            onChange={e => setFormData({...formData, cta: {...formData.cta, title: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Description</label>
                                        <textarea className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} rows="2" 
                                            value={formData.cta?.desc || ''} 
                                            onChange={e => setFormData({...formData, cta: {...formData.cta, desc: e.target.value}})} 
                                        ></textarea>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Email Address</label>
                                        <input type="email" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.cta?.email || ''} 
                                            onChange={e => setFormData({...formData, cta: {...formData.cta, email: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Button Text</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.cta?.buttonText || ''} 
                                            onChange={e => setFormData({...formData, cta: {...formData.cta, buttonText: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Button Link</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.cta?.buttonLink || ''} 
                                            onChange={e => setFormData({...formData, cta: {...formData.cta, buttonLink: e.target.value}})} 
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                        
                        <div className="mt-5 d-flex gap-2">
                            <button type="submit" className="btn text-white fw-bold px-5" style={{ background: 'var(--eventflow-primary)' }} disabled={loading}>
                                {loading ? 'Saving...' : 'Save Page Content'}
                            </button>
                            <button type="button" className="btn btn-outline-secondary text-white" onClick={() => setIsEditing(false)}>Cancel</button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <>
            {error && <div className="alert alert-danger">{error}</div>}
            
            <div className="card shadow-lg border-0 rounded-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="card-header border-0 bg-transparent py-4 px-4 d-flex justify-content-between align-items-center">
                    <h4 className="text-white m-0" style={{ fontFamily: 'var(--eventflow-font)', fontWeight: 700 }}>CFP Page Content</h4>
                    <button onClick={() => setIsEditing(true)} className="btn btn-sm text-white fw-bold" style={{ background: 'var(--eventflow-primary)' }}>
                        <i className="fa fa-edit me-2"></i>Edit Content
                    </button>
                </div>
                
                <div className="card-body px-4 pb-4 text-white">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="row g-4">
                            <div className="col-12">
                                <div className="p-4 rounded" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                    <h6 className="text-uppercase text-white-50 mb-3" style={{ fontSize: '12px', letterSpacing: '2px' }}>Hero Section Preview</h6>
                                    <h3>{formData.hero?.titleLine1} <span style={{ color: 'var(--eventflow-primary)' }}>{formData.hero?.titleLine2}</span></h3>
                                    <p style={{ color: 'rgba(255,255,255,0.7)' }}>{formData.hero?.subtitle}</p>
                                    <div className="d-flex flex-wrap gap-2 mt-3">
                                        {(formData.hero?.tracks || []).map((t, i) => (
                                            <span key={i} className="badge" style={{ background: 'rgba(var(--eventflow-primary-rgb), 0.2)', border: '1px solid var(--eventflow-primary)', color: 'var(--eventflow-primary)' }}>{t}</span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="col-md-6">
                                <div className="p-4 rounded h-100" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                    <h6 className="text-uppercase text-white-50 mb-3" style={{ fontSize: '12px', letterSpacing: '2px' }}>Tracks ({formData.tracks?.length || 0})</h6>
                                    <ul className="list-unstyled m-0">
                                        {(formData.tracks || []).map((t, i) => (
                                            <li key={i} className="mb-2 pb-2 border-bottom border-secondary last-border-0">
                                                <i className={`fa ${t.icon} me-2`} style={{ color: 'var(--eventflow-primary)' }}></i>
                                                <strong>{t.title}</strong>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                            
                            <div className="col-md-6">
                                <div className="p-4 rounded h-100" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                    <h6 className="text-uppercase text-white-50 mb-3" style={{ fontSize: '12px', letterSpacing: '2px' }}>Timeline ({formData.timeline?.length || 0})</h6>
                                    <ul className="list-unstyled m-0">
                                        {(formData.timeline || []).map((t, i) => (
                                            <li key={i} className="mb-2 pb-2 border-bottom border-secondary last-border-0">
                                                <span className="text-white-50 me-3" style={{ display: 'inline-block', width: '100px' }}>{t.date}</span>
                                                <strong>{t.label}</strong>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
